import React, { useCallback, useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import { Box, Button, Grid, Typography } from '@material-ui/core';
import { Cached } from '@material-ui/icons';

import SubHeader from '../../../components/Header/SubHeader.component';
import Loader from '../../../components/LoaderSplash/Loader.component';
import MultiProduct from '../../../components/PolicySelection/MultiProduct.component';
import PolicyList from '../../../components/PolicySelection/PolicyList.component';
import { useAuthDataContext } from '../../../context/auth/auth.provider';
import { useVehiclesContext } from '../../../context/vehicles/vehicles.provider';
import { dataLayerPush } from '../../../helpers/dataLayerPush';
import {
  POLICIES_QUERY,
  policiesQuery,
} from '../../../query/policies/policies';
import { autoSynchronizationPolicies } from '../../../services/policy/policy.service';
import { myProducts as getProductsService } from '../../../services/products/products.service';
import { useProductNavigationStore } from '../../../stores/Navigation/product-navigation.store';

const AutoSyncButton = (props: any) => {
  const { setLoading, showSincroButton } = props;

  const synchronize = () => {
    setLoading(true);
    autoSynchronizationPolicies()
      .then()
      .finally(() => setLoading(false));
  };

  return (
    <>
      {showSincroButton && (
        <Grid container justify="center" style={{ marginTop: '50px' }}>
          <Button variant="contained" color="primary" onClick={synchronize}>
            <Cached style={{ marginRight: '5px' }} />
            <Typography display="inline"> Actualizar mis pólizas </Typography>
          </Button>
        </Grid>
      )}
    </>
  );
};

const NoPolicy = (props: any) => {
  const { loading, setLoading, showSincroButton } = props;
  const history = useHistory();
  const { embed } = useAuthDataContext();
  const redirectHome = () => {
    embed
      ? window.parent.postMessage({ return: 'overview' }, '*')
      : history.push('/dashboard');
  };

  return (
    <>
      <SubHeader
        content={
          <Box>
            <Typography variant="h6">
              {' '}
              ¿Qué póliza querés consultar?{' '}
            </Typography>
          </Box>
        }
        goBack={redirectHome}
      />
      <Grid container justify="center">
        <Grid item xs={11}>
          <Grid container> No hay pólizas a consultar </Grid>
        </Grid>
      </Grid>
      {!loading && (
        <AutoSyncButton
          setLoading={setLoading}
          showSincroButton={showSincroButton}
        />
      )}
    </>
  );
};

const Documentation = () => {
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const { user, policy_id, product_code, plate_number } = useAuthDataContext();
  const {
    getPolicyById,
    policy,
    loading: singlePolicyLoading,
  } = useVehiclesContext();

  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);

  let lastPageEmpty = 0;

  // // fetchNextPage => this set new policies for Infinite list
  // const { data: policies, fetchNextPage } = useInfiniteQuery(
  //   [
  //     POLICIES_QUERY,
  //     {
  //       name: 'product_codes',
  //       value: selectedProducts.join(',')
  //     },
  //     policy_id && {
  //       name: 'id',
  //       value: policy_id,
  //     },
  //   ].filter(Boolean),
  //   policiesQuery,
  //   {
  //     getNextPageParam: (lastPage) => {
  //       lastPageEmpty = lastPage.length;
  //     },
  //   }
  // );

  const hasValidPolicy =
    policy && Object.keys(policy).length > 0 && policy.id === policy_id;

  useEffect(() => {
    if (!policy_id || hasValidPolicy) return;
    getPolicyById(policy_id);
  }, [getPolicyById, hasValidPolicy, policy_id]);

  // 2. Query de múltiples pólizas
  const {
    data: multiPoliciesData,
    fetchNextPage,
    isLoading: multiPolicyLoading,
  } = useInfiniteQuery(
    [
      POLICIES_QUERY,
      { name: 'product_codes', value: selectedProducts.join(',') },
    ],
    policiesQuery,
    {
      enabled: !policy_id,
      getNextPageParam: (lastPage) => lastPage.length,
    }
  );

  // 3. Normalizar salida para renderizado uniforme
  const normalizedPages = policy_id
    ? policy && policy.id
      ? [[policy]]
      : []
    : multiPoliciesData?.pages ?? [];

  const isLoading = policy_id ? singlePolicyLoading : multiPolicyLoading;

  const [onlyCarProduct, setOnlyCarProduct] = useState(false);
  const [showSincroButton, setShowSincroButton] = useState(false);

  const isMultiProduct = useProductNavigationStore(
    (state) => state.isMultiProduct
  );

  const setIsMultiProduct = useProductNavigationStore(
    (state) => state.setIsMultiProduct
  );

  const setClientType = useProductNavigationStore(
    (state) => state.setClientType
  );

  useEffect(() => {
    dataLayerPush('inicio-documentacion', user?.id);
  }, [user]);

  const getProducts = useCallback(async () => {
    await getProductsService()
      .then((response) => {
        !product_code
          ? setProducts(response.data)
          : setProducts(
              response.data.filter((element) => element.code === product_code)
            );
        setLoading(false);
      })
      .catch((error: any) => {
        setHasError(true);
      })
      .finally(() => setLoading(false));
    setSelectedProducts([]);
  }, [product_code]);

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  useEffect(() => {
    if (!policy_id && products?.length > 1) {
      setIsMultiProduct(true);
    } else {
      setShowSincroButton(true);
    }

    if (
      products?.length === 1 &&
      products.find((element) => element.code === 4)
    )
      setOnlyCarProduct(true);

    if (products?.find((element) => element.code === 4)) {
      setShowSincroButton(false);
    } else {
      setShowSincroButton(true);
    }
  }, [products, setIsMultiProduct, policy_id, product_code]);

  useEffect(() => {
    setClientType(normalizedPages.flat() || [], products);
  }, [products, normalizedPages, setClientType]);

  // this is executed when user clicks a product
  const filterProducts = useCallback((value) => {
    setSelectedProducts(value);
    setLoading(false);
  }, []);

  const finishSelection = (value) => {
    dataLayerPush('documentacion_producto', user?.id, {
      fieldName: 'params',
      fieldValue: value,
    });
    setIsMultiProduct(false);

    dataLayerPush('documentacion_poliza_consulta', user?.id);

    selectedProducts.includes(4) && selectedProducts.length === 1
      ? setOnlyCarProduct(true)
      : setOnlyCarProduct(false);
  };

  return (
    <>
      <Box display={loading || isLoading || !!plate_number ? 'block' : 'none'}>
        <Loader action={'Cargando información'}></Loader>
      </Box>

      {(!loading && !isLoading) || !hasError ? (
        <Box>
          {isMultiProduct ? (
            <>
              <MultiProduct
                header_title={'Seleccionar producto'}
                subtitle={'Accedé a tu documentación'}
                filterProducts={filterProducts}
                finishSelection={finishSelection}
              />
              <AutoSyncButton
                setLoading={setLoading}
                showSincroButton={showSincroButton}
              />
            </>
          ) : (
            <>
              {normalizedPages.flat().length >= 1 ? (
                <>
                  <PolicyList
                    header_title={
                      !onlyCarProduct
                        ? '¿Qué póliza querés consultar?'
                        : '¿Qué vehículo querés consultar?'
                    }
                    section_from={'documentation'}
                    policies={normalizedPages.flat() || []}
                    lastPageEmpty={lastPageEmpty}
                    fetchNextPage={!policy_id ? fetchNextPage : undefined}
                    onlyCarProduct={onlyCarProduct}
                    loading={loading}
                    policyId={policy_id}
                    plateNumber={plate_number}
                  />
                </>
              ) : (
                <>
                  <NoPolicy
                    loading={loading}
                    setLoading={setLoading}
                    showSincroButton={showSincroButton}
                  />
                </>
              )}
            </>
          )}
        </Box>
      ) : (
        <>
          <NoPolicy
            loading={loading}
            setLoading={setLoading}
            showSincroButton={showSincroButton}
          />
        </>
      )}
    </>
  );
};

export default Documentation;
